import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { FadeIn } from "../ui/FadeIn";

const WP_API =
  "https://www.golpopular.com/wp-json/wp/v2/posts?search=Kela&per_page=3&orderby=date&order=desc&_fields=id,title,content";

interface VideoItem {
  id: number;
  title: string;
  src: string;
}

function extractVideoSrc(html: string): string {
  // WordPress block embeds video as <video src="..."> (no <source> child)
  const match = html.match(/<video[^>]+src="([^"]+\.mp4[^"]*)"/i);
  return match ? match[1] : "";
}

function decodeHtml(html: string): string {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

function stripHtml(html: string): string {
  return decodeHtml(html.replace(/<[^>]+>/g, "")).trim();
}

export function KelaEnAccionSection() {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    fetch(WP_API)
      .then((r) => r.json())
      .then((posts: { id: number; title: { rendered: string }; content: { rendered: string } }[]) => {
        const parsed = posts
          .map((p) => ({
            id: p.id,
            title: stripHtml(p.title.rendered),
            src: extractVideoSrc(p.content.rendered),
          }))
          .filter((v) => v.src !== "");
        setVideos(parsed);
      })
      .catch((err) => console.error("[KelaEnAccion] fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  const total = videos.length;
  const multiple = total > 1;
  const current = videos[active];

  const goTo = (index: number) => {
    setActive(((index % total) + total) % total);
  };

  return (
    <section className="bg-gradient-to-b from-brand-100 via-brand-50 to-brand-100 py-16 sm:py-24">
      <div className="section-shell">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-brand-800 sm:text-4xl">Kela en acción</h2>
          <p className="mt-4 text-base text-ink sm:text-lg">
            Análisis, entrevistas y reacciones generadas a partir de lo que pasó en el partido. Una
            nueva forma de revivir la fecha, destacar jugadores y darle más visibilidad al fútbol
            amateur dentro de +10.
          </p>
        </FadeIn>

        {loading ? (
          <div className="mt-10 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-200 border-t-brand-700" />
          </div>
        ) : total === 0 ? null : (
          <>
            <div className={`relative mx-auto mt-10 max-w-4xl ${multiple ? "px-10 lg:px-14" : ""}`}>
              {multiple ? (
                <>
                  <button
                    type="button"
                    onClick={() => goTo(active - 1)}
                    className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-borderSoft bg-white p-2 text-brand-700 shadow-sm transition hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 lg:left-1"
                    aria-label="Video anterior"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => goTo(active + 1)}
                    className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-borderSoft bg-white p-2 text-brand-700 shadow-sm transition hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 lg:right-1"
                    aria-label="Video siguiente"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              ) : null}

              <FadeIn delay={0.08}>
                <div className="overflow-hidden rounded-2xl border border-borderSoft bg-white/80 shadow-soft backdrop-blur-sm">
                  <video
                    key={current.id}
                    className="aspect-video w-full bg-brand-50 object-contain"
                    controls
                    playsInline
                    preload="metadata"
                    poster="/portada_kela.png"
                    src={current.src}
                  />
                  {current.title ? (
                    <p className="border-t border-borderSoft bg-white/90 px-4 py-3 text-center text-sm font-medium text-ink">
                      {current.title}
                    </p>
                  ) : null}
                </div>
              </FadeIn>
            </div>

            {multiple ? (
              <div className="mt-6 flex items-center justify-center gap-2">
                {videos.map((video, index) => (
                  <button
                    key={video.id}
                    type="button"
                    onClick={() => setActive(index)}
                    className={`h-2.5 rounded-full transition ${
                      active === index ? "w-7 bg-brand-700" : "w-2.5 bg-brand-100 hover:bg-brand-500"
                    }`}
                    aria-label={`Ir al video ${index + 1}`}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
